/**
 * Notes de version — sélection de langue et partage par formule.
 *
 * ⚠️ CE FICHIER EST LE SEUL ENDROIT DU CHANGELOG QUI INTERROGE LE PLAN.
 * `useReleaseSplit` appelle `hasFeature` lui-même ; ni la page ni la carte ne
 * reçoivent de drapeau de formule en prop. Le dépôt a déjà payé trois fois, en
 * deux jours, la dérive inverse : un drapeau de plan redevenu une prop à chaque
 * découpage, qui garde le comportement et perd la raison.
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useTenantConfig } from '@/hooks/useTenantConfig'
import type { Formule, Item, Release, Texte } from '@/data/releases'

/** Choisit la langue publiée. Repli sur le français : la note existe toujours en FR. */
export function texte(t: Texte, langue: string): string {
    return langue.toLowerCase().startsWith('en') ? t.en : t.fr
}

/** Les cinq rubriques, dans l'ordre de lecture. */
export const RUBRIQUES = [
    'nouveautes',
    'ameliorations',
    'performance',
    'correctifs',
    'securite',
] as const

export type Rubrique = (typeof RUBRIQUES)[number]

/** Tous les éléments d'une version, à plat, rubrique par rubrique. */
function aplatir(release: Release): Array<{ rubrique: Rubrique; item: Item }> {
    return RUBRIQUES.flatMap((r) => (release[r] ?? []).map((item) => ({ rubrique: r, item })))
}

export interface PartageRelease {
    /** Ce que ce tenant possède : rendu en pleine forme, dans les rubriques. */
    servies: Record<Rubrique, Item[]>
    /** Ce qu'il ne possède pas : rangé dans le tiroir de pied de carte. */
    horsFormule: Item[]
    /** L'unique élément hors formule promu avec sa phrase et son renvoi. */
    vedette: Item | null
    /** Comptes par étiquette de formule, pour la ligne « Également… ». */
    comptes: Array<{ formule: Formule; nombre: number }>
}

const VIDE: Record<Rubrique, Item[]> = {
    nouveautes: [],
    ameliorations: [],
    performance: [],
    correctifs: [],
    securite: [],
}

/**
 * Partage les éléments d'une version entre ce que le tenant possède et le reste.
 *
 * ⚠️ TANT QUE `isLoading` EST VRAI, ON NE DÉCIDE RIEN : tout est rendu en pleine
 * forme et le tiroir reste vide. La raison est écrite dans `useTenantConfig` —
 * le repli de chargement vaut `{ plan: 'business', effective_features: [] }`,
 * un jeu incohérent avec lui-même où `hasFeature` répond NON à tort. Décider
 * pendant le chargement rangerait dans le tiroir des capacités que le client
 * possède, puis les en sortirait : la pire chose sur une page commerciale.
 *
 * ⚠️ Le partage se fait par `hasFeature(item.feature)`, JAMAIS en comparant
 * `item.formule` au palier du tenant (`isBusiness`, `plan === …`). Deux raisons,
 * toutes deux mesurées : le palier échoue OUVERT pendant le chargement, et un
 * tenant Focus peut posséder une capacité Boost via `stripe_addons` ou
 * `manual_grants`. La règle est celle de `navigation.ts` (SALE-025) : « le plan
 * passe par `feature`, jamais par un test de palier ».
 */
export function useReleaseSplit(release: Release): PartageRelease {
    const { hasFeature, isLoading } = useTenantConfig()

    return useMemo(() => {
        const servies: Record<Rubrique, Item[]> = { ...VIDE }
        RUBRIQUES.forEach((r) => {
            servies[r] = []
        })
        const horsFormule: Item[] = []

        aplatir(release).forEach(({ rubrique, item }) => {
            const possede = isLoading || !item.feature || hasFeature(item.feature)
            if (possede) servies[rubrique].push(item)
            else horsFormule.push(item)
        })

        const vedette = horsFormule.find((i) => 'vedette' in i && i.vedette) ?? null
        const comptes: Array<{ formule: Formule; nombre: number }> = (
            ['boost', 'business'] as const
        )
            .map((formule) => ({
                formule,
                nombre: horsFormule.filter((i) => i.formule === formule).length,
            }))
            .filter((c) => c.nombre > 0)

        return { servies, horsFormule, vedette, comptes }
    }, [release, hasFeature, isLoading])
}

/**
 * Nombre d'éléments hors formule sur les douze derniers mois glissants.
 *
 * Sert l'unique ligne de rappel en tête de page. Rend 0 quand le tenant possède
 * tout — la page est alors, à l'octet près, celle d'un tenant qui n'a rien à
 * découvrir : pas de ligne, pas de tiroir, pas d'étiquette.
 */
export function useCumulDouzeMois(releases: Release[]): { servies: number; horsFormule: number } {
    const { hasFeature, isLoading } = useTenantConfig()

    return useMemo(() => {
        if (isLoading) return { servies: 0, horsFormule: 0 }

        const limite = new Date()
        limite.setMonth(limite.getMonth() - 12)

        let servies = 0
        let horsFormule = 0
        releases
            .filter((r) => new Date(r.date) >= limite)
            .forEach((r) =>
                aplatir(r).forEach(({ item }) => {
                    if (!item.feature || hasFeature(item.feature)) servies += 1
                    else horsFormule += 1
                }),
            )
        return { servies, horsFormule }
    }, [releases, hasFeature, isLoading])
}

/** Formate une date de publication dans la langue de l'interface. */
export function useFormatDate(): (iso: string) => string {
    const { i18n } = useTranslation()
    return useMemo(() => {
        const fmt = new Intl.DateTimeFormat(i18n.language, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        })
        return (iso: string) => fmt.format(new Date(iso))
    }, [i18n.language])
}
